import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, quantitySchema, versionSchema } from './common.js'
import { money, toMoney } from './money.js'
import type { ProjectStatus, ProjectUserRef } from './projects.js'
import { lineSerialsSchema } from './serials.js'
import type { DocumentApproval } from './workflows.js'

/**
 * Projects release P2: budget and costs (docs/refactor/projects/02-cost-and-budget.md, P2_BREAKDOWN.md).
 * Costs reach a project through tags on purchase lines, supplier allowance lines and expenses
 * (cost-objects.ts), material issues from stock and cost entries. Actuals are read from the
 * `project_facts` view over POSTED documents; budgets are versioned copies of node × category lines.
 * Money travels as decimal strings; every amount here needs `projects.financials`.
 */

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nullableMoney = moneySchema.nullish().transform((v) => v ?? null)
const nonNegative = (v: string) => Number(v) >= 0
const positive = (v: string) => Number(v) > 0
const positiveQuantity = quantitySchema.refine(positive, 'Quantity must be above zero')

// ── refs ─────────────────────────────────────────────────────────────────────

export interface ProjectRefLite {
  id: string
  code: string
  name: string
  nameAr: string | null
  status: ProjectStatus
}
export interface WbsNodeRef {
  id: string
  outline: string
  name: string
  nameAr: string | null
  archived: boolean
}
export interface CostCategoryRef {
  id: string
  code: string
  name: string
  nameAr: string | null
  class: ProjectCostClass
  archived: boolean
}
export interface ProjectAccountRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

// ── cost categories ──────────────────────────────────────────────────────────

/** What kind of cost a category holds (cost by class on the project page; labour for time later). */
export const PROJECT_COST_CLASSES = [
  'LABOUR',
  'MATERIAL',
  'SUBCONTRACT',
  'EQUIPMENT',
  'EXPENSE',
  'OVERHEAD',
  'OTHER',
] as const
export const projectCostClassSchema = z.enum(PROJECT_COST_CLASSES)
export type ProjectCostClass = z.infer<typeof projectCostClassSchema>

const costCategoryFields = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Required')
    .max(20)
    .regex(/^[A-Za-z0-9_-]+$/, 'Letters, digits, dash and underscore'),
  name: requiredText(120),
  nameAr: optionalText(120),
  class: projectCostClassSchema,
  /** Costs in it are billable on time-and-materials projects by default (P3). */
  billableDefault: z.boolean().default(false),
  /** Left out of cost-to-cost progress (e.g. materials bought far ahead). */
  progressExcluded: z.boolean().default(false),
  /**
   * An EXPENSE account for the category's project postings (goods delivered to the site, material
   * issues, cost entries). null: the account mapped to the `project_costs` role.
   */
  accountId: nullableUuid,
})
export const projectCostCategoryInputSchema = costCategoryFields
export type ProjectCostCategoryInput = z.input<typeof projectCostCategoryInputSchema>
export const projectCostCategoryUpdateSchema = costCategoryFields.extend({ version: versionSchema })
export type ProjectCostCategoryUpdate = z.input<typeof projectCostCategoryUpdateSchema>
export const projectCostCategoryListQuerySchema = z.object({ archived: z.stringbool().optional() })

export interface ProjectCostCategory {
  id: string
  code: string
  name: string
  nameAr: string | null
  class: ProjectCostClass
  billableDefault: boolean
  progressExcluded: boolean
  account: ProjectAccountRef | null
  archived: boolean
  version: number
}

/** Added on the module's first use (insert … on conflict do nothing), then edited by the company. */
export const PROJECT_COST_CATEGORY_SEED: readonly {
  code: string
  name: string
  nameAr: string
  class: ProjectCostClass
  billableDefault: boolean
}[] = [
  { code: 'LAB', name: 'Labour', nameAr: 'العمالة', class: 'LABOUR', billableDefault: true },
  { code: 'MAT', name: 'Materials', nameAr: 'المواد', class: 'MATERIAL', billableDefault: true },
  { code: 'SUB', name: 'Subcontract', nameAr: 'مقاولات الباطن', class: 'SUBCONTRACT', billableDefault: true },
  { code: 'EQP', name: 'Equipment', nameAr: 'المعدات', class: 'EQUIPMENT', billableDefault: true },
  { code: 'EXP', name: 'Expenses', nameAr: 'المصروفات', class: 'EXPENSE', billableDefault: true },
  {
    code: 'OVH',
    name: 'Overhead',
    nameAr: 'المصروفات غير المباشرة',
    class: 'OVERHEAD',
    billableDefault: false,
  },
]

/**
 * Where a cost's category comes from when the clerk leaves it empty (02 §2.3): the product category
 * (nearest mapped ancestor), the line's account, the expense category, then these defaults per source.
 */
export const PROJECT_COST_SOURCES = [
  'goods',
  'services',
  'expense',
  'materialIssue',
  'time',
  'salesCogs',
] as const
export type ProjectCostSource = (typeof PROJECT_COST_SOURCES)[number]
/** The seeded category each source falls back to while its default is null. */
export const PROJECT_COST_SOURCE_SEED_CODE: Record<ProjectCostSource, string> = {
  goods: 'MAT',
  services: 'SUB',
  expense: 'EXP',
  materialIssue: 'MAT',
  time: 'LAB',
  salesCogs: 'MAT',
}
export const projectCategoryDefaultsSchema = z.object({
  goods: nullableUuid,
  services: nullableUuid,
  expense: nullableUuid,
  materialIssue: nullableUuid,
  time: nullableUuid,
  salesCogs: nullableUuid,
})
export type ProjectCategoryDefaults = z.output<typeof projectCategoryDefaultsSchema>
export const DEFAULT_PROJECT_CATEGORY_DEFAULTS: ProjectCategoryDefaults = projectCategoryDefaultsSchema.parse(
  {},
)

// ── category mapping ─────────────────────────────────────────────────────────

export const PROJECT_COST_MAP_SOURCES = ['EXPENSE_CATEGORY', 'PRODUCT_CATEGORY', 'ACCOUNT'] as const
export const projectCostMapSourceSchema = z.enum(PROJECT_COST_MAP_SOURCES)
export type ProjectCostMapSource = z.infer<typeof projectCostMapSourceSchema>

/** `PUT /project-cost-categories/map`: the whole map, replaced. One entry per source record. */
export const projectCostCategoryMapSchema = z
  .object({
    entries: z
      .array(
        z.object({
          sourceKind: projectCostMapSourceSchema,
          sourceId: z.uuid(),
          costCategoryId: z.uuid(),
        }),
      )
      .max(2000),
  })
  .superRefine((v, ctx) => {
    const seen = new Set<string>()
    v.entries.forEach((e, i) => {
      const key = `${e.sourceKind}:${e.sourceId}`
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: ['entries', i], message: 'Mapped twice' })
      seen.add(key)
    })
  })
export type ProjectCostCategoryMapInput = z.input<typeof projectCostCategoryMapSchema>

export interface ProjectCostCategoryMapEntry {
  sourceKind: ProjectCostMapSource
  /** The expense category, product category or account (code null for categories). */
  source: { id: string; code: string | null; name: string; nameAr: string | null; archived: boolean }
  costCategory: CostCategoryRef
}

// ── budgets ──────────────────────────────────────────────────────────────────

/**
 * DRAFT → (submit) PENDING → APPROVED, or DRAFT → APPROVED directly while budget approval is off.
 * PENDING → DRAFT on withdraw or when the workflow rejects or returns it. Approving a version makes the
 * previous APPROVED one SUPERSEDED. Only a DRAFT changes or is deleted.
 */
export const PROJECT_BUDGET_STATUSES = ['DRAFT', 'PENDING', 'APPROVED', 'SUPERSEDED'] as const
export const projectBudgetStatusSchema = z.enum(PROJECT_BUDGET_STATUSES)
export type ProjectBudgetStatus = z.infer<typeof projectBudgetStatusSchema>

/** A line's amount from its quantity and rate: round(quantity × rate, 3). */
export function budgetLineAmount(quantity: string, rate: string): string {
  return toMoney(money(quantity).times(rate))
}

export const projectBudgetLineInputSchema = z
  .object({
    /** null: the project as a whole (no plan node). */
    wbsNodeId: nullableUuid,
    costCategoryId: z.uuid(),
    /** Quantity and rate are optional and come together; then the amount is their product. */
    quantity: quantitySchema
      .refine(nonNegative, 'Cannot be negative')
      .nullish()
      .transform((v) => v ?? null),
    unit: optionalText(20),
    rate: moneySchema
      .refine(nonNegative, 'Cannot be negative')
      .nullish()
      .transform((v) => v ?? null),
    amount: moneySchema.refine(nonNegative, 'Cannot be negative'),
    note: optionalText(500),
  })
  .superRefine((l, ctx) => {
    if ((l.quantity === null) !== (l.rate === null)) {
      ctx.addIssue({ code: 'custom', path: ['rate'], message: 'Give both quantity and rate, or neither' })
    } else if (
      l.quantity !== null &&
      l.rate !== null &&
      budgetLineAmount(l.quantity, l.rate) !== toMoney(l.amount)
    ) {
      ctx.addIssue({ code: 'custom', path: ['amount'], message: 'The amount must be quantity × rate' })
    }
  })
export type ProjectBudgetLineInput = z.input<typeof projectBudgetLineInputSchema>

/** At most this many lines in one budget version. */
export const PROJECT_BUDGET_LINE_LIMIT = 2000

/**
 * `POST /projects/:id/budgets`: a new DRAFT, copied from the APPROVED version (version 1 starts
 * empty). Refused while another DRAFT or PENDING version exists (409 PROJECT_BUDGET_OPEN). Version 2
 * onward needs a reason (here or when the lines are saved, before submitting or approving).
 */
export const projectBudgetCreateSchema = z.object({ reason: optionalText(1000) })
export type ProjectBudgetCreateInput = z.input<typeof projectBudgetCreateSchema>

/** `PUT /projects/:id/budgets/:budgetId/lines`: replaces every line of a DRAFT, its reason and revenue. */
export const projectBudgetLinesSchema = z
  .object({
    version: versionSchema,
    reason: optionalText(1000),
    /** The revenue the budget plans against; null: none (internal projects). */
    revenueAmount: nullableMoney.refine((v) => v === null || nonNegative(v), 'Cannot be negative'),
    lines: z.array(projectBudgetLineInputSchema).max(PROJECT_BUDGET_LINE_LIMIT),
  })
  .superRefine((v, ctx) => {
    const seen = new Set<string>()
    v.lines.forEach((l, i) => {
      const key = `${l.wbsNodeId ?? '-'}:${l.costCategoryId}`
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines', i, 'costCategoryId'],
          message: 'This node and category are used twice',
        })
      }
      seen.add(key)
    })
  })
export type ProjectBudgetLinesInput = z.input<typeof projectBudgetLinesSchema>

/** `POST …/budgets/:budgetId/{submit,approve,withdraw}`. */
export const projectBudgetCommandSchema = z.object({ version: versionSchema })
export type ProjectBudgetCommandInput = z.input<typeof projectBudgetCommandSchema>

export interface ProjectBudgetListItem {
  id: string
  versionNo: number
  status: ProjectBudgetStatus
  reason: string | null
  /** Σ line amounts. */
  totalCost: string
  revenueAmount: string | null
  lineCount: number
  preparedBy: ProjectUserRef
  createdAt: string
  submittedAt: string | null
  submittedBy: ProjectUserRef | null
  decidedAt: string | null
  decidedBy: ProjectUserRef | null
  version: number
}

export interface ProjectBudgetLine {
  id: string
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef
  quantity: string | null
  unit: string | null
  rate: string | null
  amount: string
  note: string | null
}

/** Why a budget command is not offered although the viewer may see the budget. */
export type ProjectBudgetBlockReason =
  | 'NOT_PERMITTED'
  /** Not from this status. */
  | 'ILLEGAL_MOVE'
  /** Nobody approves a budget they prepared or submitted (four eyes). */
  | 'SELF_APPROVAL'
  /** Budget approval is on: submit it instead of approving it directly. */
  | 'APPROVAL_REQUIRED'
  /** An approval workflow decides this one (approve from My approvals). */
  | 'WORKFLOW'
  /** Version 2 onward needs a reason first. */
  | 'REASON_REQUIRED'
  /** The project is closed or cancelled. */
  | 'PROJECT_NOT_OPEN'
  | 'FEATURE_OFF'

export type ProjectBudgetCommand = 'edit' | 'delete' | 'submit' | 'approve' | 'withdraw'

export interface ProjectBudget extends ProjectBudgetListItem {
  lines: ProjectBudgetLine[]
  /** Signed change of `totalCost` against the APPROVED version (the whole cost for version 1). */
  changeVsBaseline: string
  /**
   * The approval workflow state while budget approval is on and a workflow applies (or a run exists);
   * null otherwise (four-eyes approval or approval off).
   */
  approval: DocumentApproval | null
  /** Who must approve, in words the status line shows. */
  approvalPath: 'DIRECT' | 'SECOND_PERSON' | 'WORKFLOW'
  can: Record<ProjectBudgetCommand, boolean>
  blocked: Partial<Record<ProjectBudgetCommand, ProjectBudgetBlockReason>>
}

// ── financials (budget vs actual) ────────────────────────────────────────────

export const PROJECT_FINANCIAL_GROUPINGS = ['category', 'node', 'both'] as const
export type ProjectFinancialGrouping = (typeof PROJECT_FINANCIAL_GROUPINGS)[number]
export const projectFinancialsQuerySchema = z.object({
  by: z.enum(PROJECT_FINANCIAL_GROUPINGS).default('category'),
})
export type ProjectFinancialsQuery = z.input<typeof projectFinancialsQuerySchema>

/**
 * Use = (actual + committed) ÷ budget. Below the warning threshold NORMAL; from it WARN; from the
 * critical threshold CRITICAL. NONE: nothing budgeted to compare with.
 */
export const BUDGET_BANDS = ['NONE', 'NORMAL', 'WARN', 'CRITICAL'] as const
export type BudgetBand = (typeof BUDGET_BANDS)[number]

export function budgetBand(
  usePct: string | null,
  thresholds: { warnPct: number; criticalPct: number },
): BudgetBand {
  if (usePct === null) return 'NONE'
  const use = money(usePct)
  if (use.greaterThanOrEqualTo(thresholds.criticalPct)) return 'CRITICAL'
  if (use.greaterThanOrEqualTo(thresholds.warnPct)) return 'WARN'
  return 'NORMAL'
}

export interface ProjectCostFigures {
  /** The APPROVED budget; "0.000" without one. */
  budget: string
  /** Tagged lines of draft purchases. */
  committed: string
  /** POSTED costs. */
  actual: string
  /** budget − actual − committed (negative when over). */
  remaining: string
  /** (actual + committed) ÷ budget × 100, two decimals; null when the budget is zero. May exceed 100. */
  usePct: string | null
  band: BudgetBand
}

export interface ProjectFinancialRow extends ProjectCostFigures {
  /** Stable within one response: `node:<id>`, `cat:<id>`, `node:<id>/cat:<id>`, `project`. */
  key: string
  parentKey: string | null
  /** 0 for top rows. */
  depth: number
  /** Node rows (by=node|both) and their category rows; null for the project-level row and by=category. */
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  /** Cost or commitment where the APPROVED budget has no line ("Not in budget"). */
  notInBudget: boolean
}

export interface ProjectFinancials {
  asOf: string
  /** The APPROVED version (the baseline), or null. */
  baseline: { id: string; versionNo: number; decidedAt: string | null } | null
  /** The open DRAFT or PENDING version, if any. */
  openBudget: { id: string; versionNo: number; status: 'DRAFT' | 'PENDING' } | null
  totals: ProjectCostFigures
  thresholds: { warnPct: number; criticalPct: number }
  /** The original contract value (CUSTOMER projects). */
  contractValue: string | null
  /** The baseline's revenue; budgeted margin = revenue − budget. */
  budgetRevenue: string | null
  budgetMargin: string | null
  /** Actual and committed cost by class, every class present (zeros included), in PROJECT_COST_CLASSES order. */
  byClass: { class: ProjectCostClass; actual: string; committed: string }[]
  /**
   * by=category: one row per category with a budget, cost or commitment.
   * by=node: a "project level" row (key `project`, budget and cost on no node), then the plan nodes as
   *   a tree (rolled up), in outline order.
   * by=both: the node rows, each followed by its category rows (depth + 1) for the node's own lines.
   */
  rows: ProjectFinancialRow[]
}

// ── facts (cost lines) and documents ─────────────────────────────────────────

/**
 * Fact kinds. REVENUE: project invoices and debit notes (ex-VAT subtotal) less credit notes. COLLECTION:
 * receipt allocations to project invoices (VAT-inclusive) less refunds of project credit notes (P5). Both
 * from P3.
 */
export const PROJECT_FACT_KINDS = ['COST', 'COMMITMENT', 'REVENUE', 'COLLECTION'] as const
export type ProjectFactKind = (typeof PROJECT_FACT_KINDS)[number]

/** Source documents of facts (snake_case entity names, as task links and attachments). P3/P6 add more. */
export const PROJECT_FACT_SOURCES = [
  'purchase',
  'supplier_return',
  'expense',
  'project_material_issue',
  'project_cost_entry',
  /** P3: invoices and debit notes (REVENUE; their lines' cost as COST, COGS). */
  'sales_invoice',
  /** P3: credit notes (negative REVENUE; their returned lines' cost as negative COST). */
  'credit_note',
  /** P3: receipts allocated to project invoices (COLLECTION). */
  'receipt',
  /** P3: legacy figures before go-live (03 §9). */
  'project_opening',
  /** P4: certified progress claims (REVENUE = Δgross − Δpenalty, 04 §8.12). */
  'project_claim',
  /** P5: retention forfeited (negative REVENUE, decision 4). */
  'project_retention_forfeit',
  /** P5: refunds of a project credit note's credit (negative COLLECTION, decision 32). */
  'customer_refund',
  /** P6: approved time (COST = captured cost amount, quantity in hours, unit HOUR). */
  'project_time_entry',
] as const
export type ProjectFactSource = (typeof PROJECT_FACT_SOURCES)[number]

export const PROJECT_FACT_SORT_FIELDS = ['factDate', 'amount', 'sourceNumber'] as const

/** `GET /projects/:id/facts`: the cost lines. Default sort `-factDate`. */
export const projectFactsQuerySchema = pageQuerySchema.extend({
  kind: z.enum(PROJECT_FACT_KINDS).optional(),
  sourceType: z.enum(PROJECT_FACT_SOURCES).optional(),
  costCategoryId: z.uuid().optional(),
  /** The node and every node below it. */
  wbsNodeId: z.uuid().optional(),
  /** Only facts on no plan node. */
  noNode: z.stringbool().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type ProjectFactsQuery = z.input<typeof projectFactsQuerySchema>

export interface ProjectFactParty {
  type: 'SUPPLIER' | 'CUSTOMER'
  id: string
  name: string
  nameAr: string | null
}

export interface ProjectFact {
  kind: ProjectFactKind
  sourceType: ProjectFactSource
  sourceId: string
  sourceLineId: string | null
  /** Draft purchases have no number yet. */
  sourceNumber: string | null
  factDate: string
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  description: string | null
  quantity: string | null
  /** P6: null for a time entry's row (one person's cost) without projects.rates. */
  amount: string | null
  party: ProjectFactParty | null
  /** The viewer may open the source document (otherwise the number shows as plain text). */
  openable: boolean
}

/** `GET /projects/:id/documents`: facts grouped by source document. Default sort `-date`. */
export const projectDocumentsQuerySchema = pageQuerySchema.extend({
  kind: z.enum(PROJECT_FACT_KINDS).optional(),
  sourceType: z.enum(PROJECT_FACT_SOURCES).optional(),
})
export type ProjectDocumentsQuery = z.input<typeof projectDocumentsQuerySchema>

export interface ProjectDocument {
  kind: ProjectFactKind
  sourceType: ProjectFactSource
  sourceId: string
  sourceNumber: string | null
  date: string
  party: ProjectFactParty | null
  /** Σ of the document's facts on this project; P6: null for a time entry without projects.rates. */
  amount: string | null
  lineCount: number
  openable: boolean
}

// ── material issues and returns ──────────────────────────────────────────────

/** ISSUE: stock leaves a warehouse for the project. RETURN: leftovers come back into a warehouse. */
export const MATERIAL_ISSUE_KINDS = ['ISSUE', 'RETURN'] as const
export const materialIssueKindSchema = z.enum(MATERIAL_ISSUE_KINDS)
export type MaterialIssueKind = z.infer<typeof materialIssueKindSchema>
export const MATERIAL_ISSUE_STATUSES = ['POSTED', 'VOID'] as const
export type MaterialIssueStatus = (typeof MATERIAL_ISSUE_STATUSES)[number]

export const materialIssueLineInputSchema = z.object({
  variantId: z.uuid(),
  quantity: positiveQuantity,
  /** Serial-tracked products: one per unit (whole quantity). */
  serials: lineSerialsSchema,
  /** RETURN: exactly one of these, the line it brings back. ISSUE: neither. */
  issueLineId: nullableUuid,
  purchaseLineId: nullableUuid,
})
export type MaterialIssueLineInput = z.input<typeof materialIssueLineInputSchema>

/**
 * `POST /project-material-issues`: posted on save (no drafts), numbered MI-2026-00001.
 * ISSUE: at the current average cost; node and category go to every line (category empty: resolved,
 * `materialIssue` default). RETURN: node and category must be empty; each line returns its cost to
 * where its source line put it, at the source's unit cost, never more than issued (or delivered) less
 * what came back already.
 */
export const materialIssueInputSchema = z
  .object({
    kind: materialIssueKindSchema,
    projectId: z.uuid(),
    wbsNodeId: nullableUuid,
    costCategoryId: nullableUuid,
    warehouseId: z.uuid(),
    issueDate: z.iso.date(),
    reference: optionalText(100),
    notes: optionalText(2000),
    lines: z.array(materialIssueLineInputSchema).min(1, 'Add at least one line').max(500),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'RETURN') {
      if (v.wbsNodeId !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['wbsNodeId'],
          message: 'A return goes back to where it was issued',
        })
      }
      if (v.costCategoryId !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['costCategoryId'],
          message: 'A return goes back to where it was issued',
        })
      }
    }
    v.lines.forEach((l, i) => {
      const refs = (l.issueLineId !== null ? 1 : 0) + (l.purchaseLineId !== null ? 1 : 0)
      if (v.kind === 'RETURN' && refs !== 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines', i, 'issueLineId'],
          message: 'Choose what is coming back',
        })
      }
      if (v.kind === 'ISSUE' && refs !== 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines', i, 'issueLineId'],
          message: 'Only a return refers to an earlier line',
        })
      }
    })
  })
export type MaterialIssueInput = z.input<typeof materialIssueInputSchema>

export const MATERIAL_ISSUE_SORT_FIELDS = ['issueDate', 'number', 'value'] as const

/** `GET /project-material-issues` (projects.materials or projects.financials). `q`: number, reference. */
export const materialIssueListQuerySchema = pageQuerySchema.extend({
  projectId: z.uuid().optional(),
  warehouseId: z.uuid().optional(),
  kind: materialIssueKindSchema.optional(),
  status: z.enum(MATERIAL_ISSUE_STATUSES).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type MaterialIssueListQuery = z.input<typeof materialIssueListQuerySchema>

/** `GET /project-material-issues/returnable?projectId&warehouseId`: what a RETURN can bring back. */
export const materialReturnableQuerySchema = z.object({
  projectId: z.uuid(),
  q: z.string().trim().max(200).optional(),
})
export type MaterialReturnableQuery = z.input<typeof materialReturnableQuerySchema>

export interface MaterialVariantRef {
  id: string
  sku: string
  name: string
  nameAr: string | null
  unit: string | null
  trackSerials: boolean
}

export interface MaterialReturnableLine {
  /** ISSUE: a posted material issue line. PURCHASE: a goods line of a posted purchase delivered to the site. */
  source: 'ISSUE' | 'PURCHASE'
  lineId: string
  documentId: string
  documentNumber: string
  date: string
  variant: MaterialVariantRef
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  unitCost: string
  /** Issued or delivered. */
  quantity: string
  /** Already back (material returns) or sent back to the supplier (supplier returns). */
  returned: string
  remaining: string
  /** Serial numbers still out on the project (serial-tracked issues). */
  serials: string[]
}

export interface MaterialIssueListItem {
  id: string
  number: string
  kind: MaterialIssueKind
  status: MaterialIssueStatus
  project: ProjectRefLite
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  warehouse: { id: string; code: string; name: string }
  issueDate: string
  reference: string | null
  /** Σ line values (positive for both kinds). */
  value: string
  lineCount: number
  createdBy: ProjectUserRef | null
  createdAt: string
}

export interface MaterialIssueLine {
  id: string
  lineNo: number
  variant: MaterialVariantRef
  quantity: string
  serials: string[]
  unitCost: string
  value: string
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  /** RETURN: the line it brings back. */
  returnOf: {
    source: 'ISSUE' | 'PURCHASE'
    lineId: string
    documentId: string
    documentNumber: string
  } | null
  /** ISSUE: quantity already back on posted returns. */
  returnedQuantity: string
}

export interface MaterialIssue extends MaterialIssueListItem {
  notes: string | null
  lines: MaterialIssueLine[]
  journalEntryId: string | null
  voidReason: string | null
  voidedAt: string | null
  voidedBy: ProjectUserRef | null
  /** Posted returns referring to this issue (the trail). */
  returns: { id: string; number: string; issueDate: string; value: string }[]
  can: { void: boolean }
  version: number
}

// ── cost entries ─────────────────────────────────────────────────────────────

/**
 * ALLOCATION: a cost on no document (own equipment, an overhead share): Dr each line's cost account,
 *   Cr `creditAccountId`.
 * TRANSFER: moves cost between projects, nodes or categories; lines net to zero; nothing is posted
 *   between categories on the same account.
 * OPENING: cost before go-live (legacy import); no journal entry.
 * LABOUR_POSTING: approved time cost to the books (P6). Made only by `POST /project-cost-entries/labour`
 *   (projects-time.ts), which builds its lines from approved time; the generic create refuses it. Facts
 *   leave it out: time cost is counted from the entries.
 */
export const COST_ENTRY_KINDS = ['ALLOCATION', 'TRANSFER', 'OPENING', 'LABOUR_POSTING'] as const
export const costEntryKindSchema = z.enum(COST_ENTRY_KINDS)
export type CostEntryKind = z.infer<typeof costEntryKindSchema>
/** The kinds a user records in this release. */
export const COST_ENTRY_KINDS_AVAILABLE: readonly CostEntryKind[] = ['ALLOCATION', 'TRANSFER', 'OPENING']
export const COST_ENTRY_STATUSES = ['POSTED', 'VOID'] as const
export type CostEntryStatus = (typeof COST_ENTRY_STATUSES)[number]

export const costEntryLineInputSchema = z.object({
  projectId: z.uuid(),
  wbsNodeId: nullableUuid,
  costCategoryId: z.uuid(),
  /** Signed for TRANSFER (the source line negative); above zero for the other kinds. */
  amount: moneySchema.refine((v) => Number(v) !== 0, 'Enter an amount'),
  note: optionalText(500),
})
export type CostEntryLineInput = z.input<typeof costEntryLineInputSchema>

/** `POST /project-cost-entries`: posted on save, numbered PCE-2026-00001. */
export const costEntryInputSchema = z
  .object({
    kind: costEntryKindSchema,
    entryDate: z.iso.date(),
    description: requiredText(500, 'Describe the entry'),
    reference: optionalText(100),
    /** ALLOCATION (and LABOUR_POSTING): the account credited; null for the other kinds. */
    creditAccountId: nullableUuid,
    lines: z.array(costEntryLineInputSchema).min(1, 'Add at least one line').max(200),
  })
  .superRefine((v, ctx) => {
    const needsCredit = v.kind === 'ALLOCATION' || v.kind === 'LABOUR_POSTING'
    if (needsCredit && v.creditAccountId === null) {
      ctx.addIssue({ code: 'custom', path: ['creditAccountId'], message: 'Choose the account to credit' })
    }
    if (!needsCredit && v.creditAccountId !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['creditAccountId'],
        message: 'Only an allocation credits an account',
      })
    }
    if (v.kind === 'TRANSFER') {
      if (v.lines.length < 2) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines'],
          message: 'A transfer needs a line to take from and one to add to',
        })
      }
      const net = v.lines.reduce(
        (s, l) => (moneySchema.safeParse(l.amount).success ? s.plus(l.amount) : s),
        money(0),
      )
      if (!net.isZero()) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines'],
          message: 'The lines of a transfer must add up to zero',
        })
      }
    } else {
      v.lines.forEach((l, i) => {
        if (moneySchema.safeParse(l.amount).success && !positive(l.amount)) {
          ctx.addIssue({
            code: 'custom',
            path: ['lines', i, 'amount'],
            message: 'Enter an amount above zero',
          })
        }
      })
    }
  })
export type CostEntryInput = z.input<typeof costEntryInputSchema>

export const COST_ENTRY_SORT_FIELDS = ['entryDate', 'number', 'amount'] as const

/** `GET /project-cost-entries` (projects.cost_entries or projects.financials). `q`: number, description, reference. */
export const costEntryListQuerySchema = pageQuerySchema.extend({
  projectId: z.uuid().optional(),
  kind: costEntryKindSchema.optional(),
  status: z.enum(COST_ENTRY_STATUSES).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type CostEntryListQuery = z.input<typeof costEntryListQuerySchema>

export interface CostEntryListItem {
  id: string
  number: string
  kind: CostEntryKind
  status: CostEntryStatus
  entryDate: string
  description: string
  reference: string | null
  /**
   * Σ of the positive amounts of the lines the viewer sees (a transfer shows what moved). With
   * `projectId`: that project's signed total. Null for a LABOUR_POSTING without projects.rates (it can be
   * one person's cost, P6 decision 33); sorting by amount puts those last.
   */
  amount: string | null
  /**
   * Projects of the lines the viewer sees, in line order; for a TRANSFER the projects the cost leaves
   * (negative lines) come first, so a list can show "PRJ-0005 → PRJ-0012".
   */
  projects: ProjectRefLite[]
  createdBy: ProjectUserRef | null
  createdAt: string
}

export interface CostEntryLine {
  id: string
  lineNo: number
  project: ProjectRefLite
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef
  /** Null on a LABOUR_POSTING without projects.rates (P6 decision 33). */
  amount: string | null
  /** P6: hours (four decimals) of a LABOUR_POSTING line, null without projects.rates; absent or null otherwise. */
  quantity?: string | null
  note: string | null
}

export interface CostEntry extends CostEntryListItem {
  creditAccount: ProjectAccountRef | null
  /** Only the lines of projects in the viewer's scope. */
  lines: CostEntryLine[]
  /** Lines of projects the viewer cannot see. */
  hiddenLineCount: number
  journalEntryId: string | null
  voidReason: string | null
  voidedAt: string | null
  voidedBy: ProjectUserRef | null
  can: { void: boolean }
  version: number
}

/** `POST /project-material-issues/:id/void`, `POST /project-cost-entries/:id/void`. */
export const projectCostVoidSchema = z.object({
  version: versionSchema,
  reason: requiredText(300, 'Give a reason'),
})
export type ProjectCostVoidInput = z.input<typeof projectCostVoidSchema>

// ── settings additions (group `projects`) ────────────────────────────────────

/** Default budget thresholds (legacy: 80 and 100). */
export const DEFAULT_BUDGET_WARN_PCT = 80
export const DEFAULT_BUDGET_CRITICAL_PCT = 100

/** Cross-field rules of the `projects` settings group added in P2 (422 VALIDATION_FAILED). */
export function projectThresholdErrors(s: {
  budgetWarnPct: number
  budgetCriticalPct: number
}): { path: string; message: string }[] {
  return s.budgetWarnPct < s.budgetCriticalPct
    ? []
    : [{ path: 'budgetCriticalPct', message: 'The critical level must be above the warning level' }]
}

// ── errors ───────────────────────────────────────────────────────────────────

/** Problem codes of P2 (besides PROJECT_ERROR_CODES and the common ones). */
export const PROJECT_COST_ERROR_CODES = [
  /** 422: serial-tracked goods can't be delivered to a site; issue them from stock instead. */
  'PROJECT_SERIAL_GOODS',
  /** 409: the project already has a DRAFT or PENDING budget version. */
  'PROJECT_BUDGET_OPEN',
  /** 409: only a DRAFT budget changes, is deleted or approved directly; only a PENDING one is withdrawn. */
  'PROJECT_BUDGET_ILLEGAL_MOVE',
  /** 422: version 2 onward needs a reason. */
  'PROJECT_BUDGET_REASON_REQUIRED',
  /** 422: four eyes: the preparer, an editor of its lines or the submitter cannot approve. (Shared with workflows.) */
  'SELF_APPROVAL',
  /** 422: budget approval is on, so a DRAFT is submitted, not approved directly. (Shared with workflows.) */
  'APPROVAL_REQUIRED',
  /** 422: an approval workflow run holds this version; it is decided in My approvals. (Shared with workflows.) */
  'APPROVAL_PENDING',
  /** 409: a cost category with this code exists. */
  'PROJECT_COST_CATEGORY_CODE_TAKEN',
  /** 422: the lines of a transfer do not add up to zero (also refused by the database at commit). */
  'PROJECT_COST_ENTRY_UNBALANCED',
] as const
export type ProjectCostErrorCode = (typeof PROJECT_COST_ERROR_CODES)[number]
